import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, CheckCircle2, Circle, Building2, FolderKanban } from "lucide-react";
import { format, addDays, isBefore, isAfter, startOfToday } from "date-fns";
import { cn } from "@/lib/utils";

const priorityColors = {
  low: "bg-blue-100 text-blue-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-rose-100 text-rose-700",
  urgent: "bg-rose-200 text-rose-800",
};

export default function UpcomingTasksWidget() {
  const queryClient = useQueryClient();

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list(),
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const isLoading = userLoading || tasksLoading;

  // Filter oppgaver: tildelt bruker, ikke fullført, forfaller innen 7 dager
  const upcomingTasks = tasks.filter(task => {
    if (!task.due_date || !user) return false;
    if (task.assigned_to !== user.email) return false;
    if (task.status === "completed" || task.status === "cancelled") return false;

    const dueDate = new Date(task.due_date);
    const today = startOfToday();
    const sevenDaysFromNow = addDays(today, 7);

    return !isBefore(dueDate, today) && !isAfter(dueDate, sevenDaysFromNow);
  }).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const handleMarkComplete = (task) => {
    updateTaskMutation.mutate({
      id: task.id,
      data: { ...task, status: "completed" }
    });
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">Kommende Oppgaver</h3>
          <p className="text-sm text-slate-500">Neste 7 dager</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-slate-100 rounded-lg" />
          ))}
        </div>
      ) : upcomingTasks.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Ingen kommende oppgaver</p>
        </div>
      ) : (
        <div className="space-y-2">
          {upcomingTasks.map((task) => {
            const isOverdue = new Date(task.due_date) < startOfToday();
            const daysUntilDue = Math.ceil((new Date(task.due_date) - startOfToday()) / (1000 * 60 * 60 * 24));
            
            return (
              <div
                key={task.id}
                className="p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-all"
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => handleMarkComplete(task)}
                    disabled={updateTaskMutation.isPending}
                    className="mt-0.5"
                  >
                    <Circle className="w-4 h-4 text-slate-400 hover:text-emerald-500 transition-colors" />
                  </button>

                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 text-sm mb-1 truncate">
                      {task.title}
                    </h4>
                    
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={cn("text-xs border-0", priorityColors[task.priority])}>
                        {task.priority}
                      </Badge>
                      
                      {task.project_name && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <FolderKanban className="w-3 h-3" />
                          <span className="truncate max-w-[120px]">{task.project_name}</span>
                        </div>
                      )}
                      
                      {task.related_to_name && !task.project_name && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Building2 className="w-3 h-3" />
                          <span className="truncate max-w-[120px]">{task.related_to_name}</span>
                        </div>
                      )}
                    </div>

                    <div className={cn(
                      "flex items-center gap-1 text-xs font-medium",
                      isOverdue ? "text-rose-600" : daysUntilDue <= 2 ? "text-amber-600" : "text-slate-500"
                    )}>
                      <Calendar className="w-3 h-3" />
                      <span>
                        {format(new Date(task.due_date), "d. MMM")}
                        {daysUntilDue === 0 && " (i dag)"}
                        {daysUntilDue === 1 && " (i morgen)"}
                        {daysUntilDue > 1 && ` (om ${daysUntilDue} dager)`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}